import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=unauth, obj=auth

  useEffect(() => {
    let cancelled = false;
    // Resolve the session, but only treat a real auth rejection (401/403) as
    // "signed out". Transient failures (network blip, preview pod waking, 5xx)
    // are retried so the user isn't bounced to /login on a momentary hiccup.
    const resolveSession = async (attempt = 0) => {
      try {
        const { data } = await api.get("/auth/me");
        if (!cancelled) setUser(data);
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          if (!cancelled) setUser(false);
          return;
        }
        if (attempt < 3) {
          setTimeout(() => { if (!cancelled) resolveSession(attempt + 1); }, 1500 * (attempt + 1));
        } else if (!cancelled) {
          setUser(false); // give up after retries
        }
      }
    };
    resolveSession();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // already signed out server-side — clear locally regardless
    }
    setUser(false);
  }, []);

  const value = useMemo(() => ({ user, setUser, login, logout }), [user, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
